import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import moment from 'moment';
import { AvailabilityApiService } from './availability.service';
import { PropertyService } from '../service/property.service';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { InputSwitchModule } from 'primeng/inputswitch';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectButtonModule } from 'primeng/selectbutton';

interface AvailabilitySlot {
    start: string;
    end: string;
}

@Component({
    selector: 'app-availability',
    templateUrl: './availability.component.html',
    styleUrls: ['./availability.component.scss'],
    standalone: true,
    imports: [
        DialogModule,
        CommonModule,
        ReactiveFormsModule,
        DropdownModule,
        InputTextModule,
        ButtonModule,
        TableModule,
        ToastModule,
        CalendarModule,
        FormsModule,
        AccordionModule,
        InputSwitchModule,
        IconFieldModule,
        InputIconModule,
        SelectButtonModule,
    ],
    providers: [MessageService]
})
export class AvailabilityComponent implements OnInit {
    availabilityForm!: FormGroup;
    propertyId: string = '';
    propertyTimezone: string = '';

    availabilityMode: string = 'full';
    availabilityModeOptions = [
        { label: 'Full Hours', value: 'full' },
        { label: 'Off Hours',  value: 'off'  },
    ];

    days = [
        { name: 'monday', label: 'Monday' },
        { name: 'tuesday', label: 'Tuesday' },
        { name: 'wednesday', label: 'Wednesday' },
        { name: 'thursday', label: 'Thursday' },
        { name: 'friday', label: 'Friday' },
        { name: 'saturday', label: 'Saturday' },
        { name: 'sunday', label: 'Sunday' }
    ];

    intervalOptions = [
        { label: '30 minutes', value: 30 }
    ];

    conflicts: any[] = [];
    showConflictDialog = false;
    conflictPayload: any = null;

    loading: boolean = false;
    submitting: boolean = false;
    showUnsavedAlert: boolean = false;

    constructor(
        private fb: FormBuilder,
        private propertyService: PropertyService,
        private availabilityService: AvailabilityApiService,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.propertyService.getUserInfo().subscribe(user => {
            this.propertyId = user.property_id;
            this.propertyTimezone = user.property.timezone;
            this.fetchAvailability();
            this.availabilityForm.valueChanges.subscribe(() => {
                if (this.availabilityForm.dirty && !this.showUnsavedAlert) {
                    this.showUnsavedAlert = true;
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Unsaved Changes',
                        detail: 'Click Save Availability button to apply changes.',
                        life: 4000
                    });
                }
            });
        });
    }

    markAsDirty() {
        if (!this.showUnsavedAlert) {
            this.showUnsavedAlert = true;

            // ✅ Show toast once — not continuously
            this.messageService.add({
                severity: 'warn',
                summary: 'Unsaved Changes',
                detail: 'Click Save to apply changes.',
                life: 4000
            });
        }
    }

    /** Initialize form with empty arrays for each day */
    initForm() {
        const group: any = { interval: [30, Validators.required] };
        this.days.forEach(d => {
            group[d.name] = this.fb.array([]);
            group[`${d.name}_unavailable`] = [false];
        });
        this.availabilityForm = this.fb.group(group);
    }

    createDefaultSlot(): FormGroup {
        return this.fb.group({
            start: [this.createTime(9, 0), Validators.required],
            end: [this.createTime(17, 0), Validators.required]
        });
    }

    createTime(hour: number, minute: number): Date {
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        return date;
    }

    get intervalValue(): number {
        return this.availabilityForm.get('interval')?.value || 30;
    }

    createSlot(start?: Date, end?: Date): FormGroup {
        return this.fb.group({
            start: [start || null, Validators.required],
            end: [end || null, Validators.required]
        });
    }

    getSlots(day: string): FormArray {
        return this.availabilityForm.get(day) as FormArray;
    }

    addSlot(day: string) {
        this.getSlots(day).push(this.createSlot());
        this.markAsDirty();
    }

    addPropertySlot(day: string) {
        const propertyTz = this.propertyTimezone;
        const interval = this.intervalValue;

        // Current time in property timezone
        const nowInPropertyTz = moment.tz(propertyTz).minute(0).second(0);

        // Calculate offset difference from local timezone (browser)
        const offsetMinutes = nowInPropertyTz.utcOffset() - moment().utcOffset();

        // Create JS Date in browser timezone, but shifted to property time
        //const startDateTime = moment().add(offsetMinutes, 'minutes').toDate();
        let startDateTime = moment().add(offsetMinutes, 'minutes').toDate();
        startDateTime = this.normalizeTime(startDateTime, interval);

        //const endDateTime = moment().add(offsetMinutes + 180, 'minutes').toDate(); // +3 hours
        const endDateTime = new Date(startDateTime);
        endDateTime.setMinutes(endDateTime.getMinutes() + interval * 10);

        this.getSlots(day).push(this.createSlot(startDateTime, endDateTime));
        this.markAsDirty();
    }


    removeSlot(day: string, index: number) {
        this.getSlots(day).removeAt(index);
        this.markAsDirty();
    }

    /** Toggle unavailable: hide inputs and disable Add Slot button */
    toggleUnavailable(day: string, unavailable: boolean) {
        const control = this.getSlots(day);

        if (unavailable) {
            // Disable slot inputs
            control.disable({ emitEvent: false });
        } else {
            // Enable slot inputs
            control.enable({ emitEvent: false });
            // If no slots exist, add an empty slot for UI
            //if (control.length === 0) control.push(this.createSlot());
        }

        // Update toggle value
        this.availabilityForm.get(`${day}_unavailable`)?.setValue(unavailable);
        this.availabilityForm.get(`${day}_unavailable`)?.markAsDirty();
        this.availabilityForm.get(`${day}_unavailable`)?.updateValueAndValidity({ emitEvent: true });
        this.markAsDirty();
    }

    isDayUnavailable(day: string): boolean {
        const unavailable = this.availabilityForm.get(`${day}_unavailable`)?.value;
        return unavailable ?? false;
    }

    /** Validate slots for overlaps and invalid ranges */
    validateAllSlots(): boolean {
        let valid = true;
        let errorMessages: string[] = [];
        this.days.forEach(d => {
            if (this.isDayUnavailable(d.name)) return;
            const errors: any = {};
            const slots = this.getSlots(d.name).controls;
            const sorted = slots
                .map(slot => ({
                    start: moment(slot.get('start')?.value),
                    end: moment(slot.get('end')?.value),
                    slot
                }))
                .sort((a, b) => a.start.diff(b.start));

            for (let i = 0; i < sorted.length; i++) {
                const { start, end, slot } = sorted[i];

                if (!start.isValid() || !end.isValid() || end.isSameOrBefore(start)) {
                    slot.setErrors({ invalidRange: true });
                    errors.invalidRange = 'End time must be after start time';
                    errorMessages.push(`${d.label}: End time must be later than start time.`);
                    valid = false;
                }

                if (i > 0 && start.isBefore(sorted[i - 1].end)) {
                    slot.setErrors({ overlap: true });
                    errors.overlap = 'This slot overlaps with previous slot';
                    errorMessages.push(`${d.label}: Overlapping time slots detected.`);
                    sorted[i - 1].slot.setErrors({ overlap: true });
                    valid = false;
                }

                // If there are errors, set them on the slot FormGroup
                if (Object.keys(errors).length > 0) {
                    slot.setErrors(errors);
                }
            }
        });
        if (!valid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: [...new Set(errorMessages)].join('\n'), // remove duplicates
                life: 5000
            });
        }
        return valid;
    }

    /** Fetch availability from API */
    fetchAvailability() {
        this.loading = true;
        this.availabilityService.get(this.propertyId).subscribe({
            next: (res: any) => {
                this.loading = false;
                if (res?.weeklySlots) this.patchFormWithData(res);
            },
            error: () => this.loading = false
        });
    }

    /** Patch form with API data */
    patchFormWithData(data: { weeklySlots: Record<string, AvailabilitySlot[]>; interval: number, timezone: string }) {
        const weeklySlots = data.weeklySlots || {};
        const propertyTimezone = this.propertyTimezone ?? data.timezone;
        const interval = data.interval ?? 30;

        this.days.forEach(d => {
            const daySlots = weeklySlots[d.name] || [];
            const control = this.getSlots(d.name);
            control.clear();

            const isUnavailable = daySlots.length === 0;

            // Set toggle
            this.availabilityForm.get(`${d.name}_unavailable`)?.setValue(isUnavailable);

            // Populate slots
            daySlots.forEach((slot: AvailabilitySlot) => {
                const fg = this.createSlot();
                const start = this.normalizeTime(
                    this.parsePropertyTime(slot.start, propertyTimezone),
                    interval
                );
                const end = this.normalizeTime(
                    this.parsePropertyTime(slot.end, propertyTimezone),
                    interval
                );
                fg.patchValue({
                    start: start,
                    end: end
                });
                control.push(fg);
            });

            // Enable/disable controls
            if (isUnavailable) {
                control.disable({ emitEvent: false });
            } else {
                control.enable({ emitEvent: false });
            }
        });

        // Set interval
        this.availabilityForm.get('interval')?.setValue(data.interval ?? 30);
    }

    parsePropertyTime(time: string, timeZone: string): Date {
        //return moment.tz(time, 'HH:mm', timeZone).toDate();
        const parsed = moment(time, 'HH:mm');
        return parsed.toDate();
    }

    /** Helper to parse time strings safely or fallback to default */
    parseTime(time: string | null, defaultHour: number, defaultMinute: number): Date {
        const date = new Date();

        if (!time) {
            date.setHours(defaultHour, defaultMinute, 0, 0);
            return date;
        }

        const [h, m] = time.split(':').map(Number);
        date.setHours(h, m, 0, 0);
        return date;
    }

    /** Prepare payload to send to API */
    preparePayload() {
        const v = this.availabilityForm.value;
        const payload: any = {
            propertyId: this.propertyId,
            timeZone: this.propertyTimezone,
            interval: v.interval,
            weeklySlots: {}
        };

        this.days.forEach(d => {
            const unavailable = v[`${d.name}_unavailable`];
            const slots = v[d.name] ?? [];

            // Send only slots array to backend
            payload.weeklySlots[d.name] = unavailable
                ? [] // if unavailable, send empty array
                : slots.map((slot: any) => ({
                    // start: moment(slot.start).tz(this.propertyTimezone).format('HH:mm'),
                    // end: moment(slot.end).tz(this.propertyTimezone).format('HH:mm')
                    start: moment(slot.start).format('HH:mm'),
                    end: moment(slot.end).format('HH:mm')
                }));
        });

        return payload;
    }

    onSubmit() {
        if (!this.validateAllSlots() || this.availabilityForm.invalid) {
            // this.messageService.add({
            //     severity: 'error',
            //     summary: 'Validation Error',
            //     detail: 'Please fix overlapping or invalid time ranges.'
            // });
            this.availabilityForm.markAllAsTouched();
            return;
        }
        const payload = this.preparePayload();

        this.loading = true;

        this.availabilityService.checkConflicts(payload).subscribe({
            next: conflicts => {
                this.loading = false;
                if (conflicts?.length > 0) {
                    this.showConflictDialogFn(conflicts, payload);
                } else {
                    this.saveFinal(payload);
                }
            },
            error: (err: any) => {
                this.loading = false;

                let errorMsg = err?.error?.message || 'Something went wrong';

                // ✅ Format message for UI
                errorMsg = this.formatConflictMessage(errorMsg);

                this.messageService.add({
                    severity: 'error',
                    summary: 'Time Slot Conflict',
                    detail: errorMsg,
                    life: 5000
                });
            }
        });
    }

    formatConflictMessage(message: string): string {
        // Example: "monday: slots overlap between indexes 1 and 2."
        const match = message.match(/^(\w+):\s*(.*)/i);

        if (!match) return message;

        const day = match[1].charAt(0).toUpperCase() + match[1].slice(1);
        const details = match[2]
            .replace('indexes', 'slots')
            .replace(/index(es)?/gi, 'slot')
            .replace(/[.]$/, ''); // remove period

        return `${day} has invalid time slots - ${details}. Please fix and try again.`;
    }


    showConflictDialogFn(conflicts: any[], payload: any) {
        this.conflicts = conflicts;
        this.conflictPayload = payload;
        this.showConflictDialog = true;
    }

    forceSaveChanges() {
        const payload = this.conflictPayload;
        this.loading = true;

        this.availabilityService.update(payload).subscribe({
            next: () => {
                this.loading = false;
                this.showConflictDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Availability updated successfully!'
                });
                this.availabilityForm.markAsPristine();
                this.showUnsavedAlert = false;
            },
            error: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update availability.'
                });
            }
        });
    }

    saveFinal(payload: any) {
        this.loading = true;
        this.availabilityService.update(payload).subscribe({
            next: () => {
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Availability updated successfully!'
                });
                this.availabilityForm.markAsPristine();
                this.showUnsavedAlert = false;
            },
            error: () => this.loading = false
        });
    }

    onCancel() {
        this.fetchAvailability();
    }

    getUnavailableControl(day: string): FormControl {
        return this.availabilityForm.get(`${day}_unavailable`) as FormControl;
    }

    normalizeTime(date: Date, interval: number): Date {
        const d = new Date(date);

        const minutes = d.getMinutes();
        const rounded = Math.floor(minutes / interval) * interval;

        d.setMinutes(rounded);
        d.setSeconds(0);
        d.setMilliseconds(0);

        return d;
    }
    
    onTimeChange(event: any) {
        if (!event?.value) return;

        const snapped = this.normalizeTime(
            event.value,
            this.intervalValue
        );

        event.value.setTime(snapped.getTime());
    }
}